import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Attachment } from '../../../core/models/attachment/attachment';
import { PopupService } from '../../../shared/services/popup/popup.service';
import { VideoFileFormat, PdfAndXlsxFileFormat } from '../../../core/services/constant';
import { RaiseFundApplicationActionType } from '../../../core/enum/raise-fund-application-action-type.enum';
import { FundRaiseProcess } from '../../../core/models/raise/fund-raise-process';
import { TabContent } from '../../../core/models/raise/tab-content';
import { ActivatedRoute, Router } from '@angular/router';
import { RaiseFundApplicationProcessModule } from '../../../core/enum/raise-fund-application-process-module.enum';
import { RaiseFundApplicationProcessStatusType } from '../../../core/enum/raise-fund-application-process-status-type.enum';
import { FundRaiseItem } from '../../../core/models/raise/fund-raise-item';
import { finalize } from 'rxjs/operators';
import { RaiseService } from '../../../core/services/api/raise.service';
import { ErrorService } from '../../../core/services/error.service';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UploadDocumentComponent } from '../../signup/documents/upload-document/upload-document.component';
import { BlockUiService } from '../../../shared/services/blockUi/block-ui.service';
import { FileValidationService } from '../../../core/services/file-validation.service';
import { FileUploadOption } from '../../../core/models/attachment/file-upload-option';
import { DocumentRequirementStatus } from '../../../core/enum/document-requirement-status.enum';

export const PRE_REGISTRATION_ITEM_TITLE = 'Unregistered Issuer Pre-Registration';

@Component({
    selector: 'app-process-list',
    templateUrl: './process-list.component.html',
    styleUrls: ['./process-list.component.scss'],
    standalone: false
})
export class ProcessListComponent implements OnInit {
  @Input() fundRaiseProcess: FundRaiseProcess;
  @Input() tabContent: TabContent;
  @Input() fundRaiseApplicationId: number;
  @Input() campaignCreated: boolean;
  @Output() next = new EventEmitter();
  @Output() refresh = new EventEmitter();

  isLoading: boolean = false;

  RaiseFundApplicationProcessModule: typeof RaiseFundApplicationProcessModule =
    RaiseFundApplicationProcessModule;
  RaiseFundApplicationActionType: typeof RaiseFundApplicationActionType =
    RaiseFundApplicationActionType;
  RaiseFundApplicationProcessStatusType: typeof RaiseFundApplicationProcessStatusType =
    RaiseFundApplicationProcessStatusType;

  // Group by status
  requiredForPreLiveItems: FundRaiseItem[] = [];
  requiredForLiveItems: FundRaiseItem[] = [];
  optionalItems: FundRaiseItem[] = [];
  noRequirementItems: FundRaiseItem[] = [];

  get campaignId(): number | undefined {
    return this.fundRaiseProcess?.items?.find((i) => i.campaignId != null)?.campaignId;
  }

  isDraftItem(item: FundRaiseItem): boolean {
    const isDraftAction =
      item.raiseFundApplicationActionType == RaiseFundApplicationActionType.CreateCampaign ||
      item.raiseFundApplicationActionType == RaiseFundApplicationActionType.CreateSummaryPage;

    const isDraftStatus = [
      RaiseFundApplicationProcessStatusType.None,
      RaiseFundApplicationProcessStatusType.Draft,
    ].includes(item.raiseFundApplicationProcessStatusType);

    return isDraftAction && isDraftStatus;
  }

  isDraftProcess(): boolean {
    const isDraftStatus = [
      RaiseFundApplicationProcessStatusType.None,
      RaiseFundApplicationProcessStatusType.Draft,
    ].includes(this.fundRaiseProcess.raiseFundApplicationProcessStatusType);

    return (
      this.fundRaiseProcess?.raiseFundApplicationActionType ==
        RaiseFundApplicationActionType.CreateCampaign && isDraftStatus
    );
  }

  constructor(
    private popupService: PopupService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private raiseService: RaiseService,
    private sanitizer: DomSanitizer,
    private errorService: ErrorService,
    private modalService: NgbModal,
    private blockUiService: BlockUiService,
    private fileValidationService: FileValidationService,
  ) {}

  ngOnInit(): void {
    this.hidePreRegistrationItems();
    this.splitStatutoryForms();
    this.groupByStatus();
  }

  hidePreRegistrationItems() {
    this.fundRaiseProcess = structuredClone(this.fundRaiseProcess);

    const shouldHide = (item: FundRaiseItem) =>
      (item.title || '').trim().toLowerCase() === PRE_REGISTRATION_ITEM_TITLE.toLowerCase();

    this.fundRaiseProcess.items = this.prunePreRegistrationItems(this.fundRaiseProcess.items, shouldHide);
  }

  prunePreRegistrationItems(
    items: FundRaiseItem[],
    shouldHide: (item: FundRaiseItem) => boolean,
  ): FundRaiseItem[] {
    return items.filter((item) => {
      if (shouldHide(item)) return false;

      if (item.subItems?.length) {
        item.subItems = this.prunePreRegistrationItems(item.subItems, shouldHide);
      }

      return true;
    });
  }

  groupByStatus() {
    this.fundRaiseProcess.items.forEach((item) => {
      if (item.isOptional) {
        this.optionalItems.push(item);
      } else if (item.documentRequirementStatus == DocumentRequirementStatus.RFPL) {
        this.requiredForPreLiveItems.push(this.filterSubItems(item, false));
      } else if (item.documentRequirementStatus == DocumentRequirementStatus.RFL) {
        this.requiredForLiveItems.push(this.filterSubItems(item, false));
      } else {
        this.noRequirementItems.push(item);
      }
    });
  }

  filterSubItems(item: FundRaiseItem, getIsOptional: boolean) {
    return {
      ...item,
      subItems: item.subItems.filter((subItem) => subItem.isOptional === getIsOptional),
    };
  }

  // PLT-1302 some child items in the statutory forms are optional, then should remain in the optional section
  // Only those that are required should be moved to the required section
  splitStatutoryForms() {
    const fundRaiseProcessCopy = structuredClone(this.fundRaiseProcess);
    const statutoryForm = fundRaiseProcessCopy.items.find((item) => item.id === 92);

    if (statutoryForm === undefined || statutoryForm?.isOptional) return;

    // Create another parent for the optional items
    const duplicateStatutoryForm = {
      ...statutoryForm,
      isOptional: true,
      subItems: statutoryForm?.subItems.filter((subItem) => subItem.isOptional),
    };

    fundRaiseProcessCopy.items.push(duplicateStatutoryForm);

    this.fundRaiseProcess = fundRaiseProcessCopy;
  }

  async selectFile(file: File, item: FundRaiseItem) {
    let uploadOption: FileUploadOption = {
      fileType: PdfAndXlsxFileFormat,
      fileSizeLimit: 30 * 1024 * 1024, // 30 MB
      errorMsg: 'common.onlyPdfAndXlasxAccepted',
    };

    if (item.raiseFundApplicationActionType == RaiseFundApplicationActionType.UploadCampaignVideo) {
      uploadOption = {
        fileType: VideoFileFormat,
        errorMsg: 'common.onlyVideoAccepted',
      };
    }

    if (await this.fileValidationService.validFile(file, uploadOption)) {
      item.attachment = file;
      item.attachmentUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
      this.uploadAttachment(item, item.id);
    }
  }

  uploadAttachment(item: FundRaiseItem, stepId: number, documentName?: string) {
    this.blockUiService.open();

    this.raiseService
      .uploadAttachment(item.attachment, this.fundRaiseApplicationId, stepId, documentName)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe(
        (data) => {
          item.attachmentUrl = data.url;
          this.refresh.emit();
        },
        (err) => {
          this.errorService.showError(err);
          this.deleteAttachment(stepId);
        },
      );
  }

  removeAttachment(document: Attachment) {
    document.file = null;
    this.refresh.emit();
  }

  viewDetails(url: string) {
    window.open(url);
  }

  nextStep() {
    if (
      this.tabContent.tab == RaiseFundApplicationProcessModule.OfferCreation &&
      !this.campaignCreated
    ) {
      this.router.navigate(['/raise/new-campaign']);
    }

    this.next.emit();
  }

  getAcceptType(raiseFundApplicationActionType: RaiseFundApplicationActionType) {
    return raiseFundApplicationActionType == RaiseFundApplicationActionType.UploadDocument
      ? PdfAndXlsxFileFormat.toString()
      : VideoFileFormat.toString();
  }

  makePayment() {
    this.router.navigate(['make-payment', this.fundRaiseApplicationId], {
      relativeTo: this.activatedRoute,
    });
  }

  uploadAdditionalDocument(item: FundRaiseItem) {
    const option: any = {
      size: 'md',
      centered: true,
      windowClass: 'modal-550',
      backdropClass: 'backdrop-popup',
    };

    let document = {} as Attachment;
    const modalRef = this.modalService.open(UploadDocumentComponent, option);
    modalRef.componentInstance.document = document;
    modalRef.componentInstance.returnFile = true;
    modalRef.componentInstance.fileTypes = PdfAndXlsxFileFormat;
    modalRef.componentInstance.fileSizeLimit = 31457280;
    modalRef.result.then(
      (document: Attachment) => {
        item.attachment = document.file;
        this.uploadAttachment(item, item.id, document.fileName);
      },
      () => {},
    );
  }

  // FRC-869 Withdraw a campaign page that is still pending admin review. The server only
  // permits this while the step is Pending, so on failure we refresh to show whatever the
  // real state now is (e.g. the admin approved or rejected it first).
  cancelSubmission(item: FundRaiseItem) {
    this.popupService
      .confirm('raise.withdrawSubmissionMessage', {
        titleLabel: 'raise.withdrawSubmissionTitle',
        okLabel: 'raise.withdrawSubmission',
        cancelLabel: 'common.cancel',
      })
      .then(
        () => {
          this.blockUiService.open();

          this.raiseService
            .cancelCampaignSubmission(this.fundRaiseApplicationId, item.campaignId)
            .pipe(finalize(() => this.blockUiService.close()))
            .subscribe(
              () => {
                this.refresh.emit();
              },
              (err) => {
                this.errorService.showError(err);
                this.refresh.emit();
              },
            );
        },
        () => {},
      );
  }

  removeAttachmentByAttachmentId(attachmentId: number, parentId: number) {
    this.popupService.confirm('company.confirmDeleteDocument').then(
      () => {
        this.blockUiService.open();

        this.raiseService
          .deleteAttachmentByAttachmentId(this.fundRaiseApplicationId, parentId, attachmentId)
          .pipe(finalize(() => this.blockUiService.close()))
          .subscribe(
            (data) => {
              this.refresh.emit();
            },
            (err) => {
              this.errorService.showError(err);
            },
          );
      },
      () => {},
    );
  }

  deleteAttachment(stepId: number) {
    this.blockUiService.open();

    this.raiseService
      .deleteAttachment(this.fundRaiseApplicationId, stepId)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe(
        (data) => {
          this.refresh.emit();
        },
        (err) => {
          this.errorService.showError(err);
        },
      );
  }
}
